// =============================================================================
// Backorder parts wizard (spec: backorder-ui-flow.html).
//
// NAMING. The user-facing word is BACKORDER everywhere, and the two terms are
// not interchangeable: a backorder is a CUSTOMER's order for goods that are not
// available yet and will ship later, while a reorder is the company topping up
// its own stock from a vendor. What this wizard does — add components to an
// existing sales order at $0 for a later, separate shipment — is the first one.
// Everything else in the stack already said so: the spec is backorder-ui-flow,
// the endpoint is /backorder-lines, the line badge reads "Backorder · No-charge"
// and this modal's own subtitle said "components on backorder" underneath a
// title that said "reorder". Only the labels were wrong; they now match.
// The component and file are still named ReorderPartsModal — renaming them is a
// separate, purely internal change and touches OrderView's import.
//
// Launched from an order's system (S9…) line. Steps:
//   1 Select  — the system's BOM components (GET /api/Part/{system}) with
//               multi-select checkboxes + per-row qty + optional shared note
//   2 Review  — edit qty / remove / edit note; nothing saved yet
//   3 Confirm — spells out: $0 lines, no-charge, separate shipment, the $0
//               invoice auto-suppresses (no email, no reminder, no amount due)
//   Submit    — POST /api/sales-orders/{n}/backorder-lines (all-or-nothing;
//               statuses Added / AlreadyPresent / Rejected / RolledBack)
//   On success ONLY when ≥1 line was Added: POST calculate-tax, then the
//   parent refreshes the order (no full-page reload). On failure: error
//   banner + per-line reasons, modal stays open to fix & resubmit.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { apiFetch } from "../utils/auth";

const API_URL = process.env.REACT_APP_API_URL;

export default function ReorderPartsModal({ show, onHide, orderNum, systemPart, systemQty = 1, onDone }) {
  const [step, setStep] = useState("select");          // select | review | confirm | error
  const [bom, setBom] = useState(null);                // [{partNum, desc, qtyPer, unitPrice}]
  const [bomError, setBomError] = useState(null);
  const [sel, setSel] = useState({});                  // partNum → qty
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState(null);        // failed response body

  // Load the system's BOM on open (tolerates both JSON casings, dedupes).
  useEffect(() => {
    if (!show || !systemPart) return;
    setStep("select"); setSel({}); setNote(""); setFilter(""); setFailure(null); setBom(null); setBomError(null);
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/Part/${encodeURIComponent(systemPart)}`);
        const data = await r.json().catch(() => null);
        if (!r.ok || !Array.isArray(data)) throw new Error(`No components found for ${systemPart} (HTTP ${r.status}).`);
        const seen = new Set();
        const rows = [];
        for (const b of data) {
          const p = (b.mtlPartNum || b.MtlPartNum || "").trim();
          if (!p || seen.has(p.toUpperCase())) continue;
          seen.add(p.toUpperCase());
          rows.push({
            partNum: p,
            desc: b.mtlPartDescription || b.MtlPartDescription || "",
            qtyPer: b.qtyPer ?? b.QtyPer ?? 1,
            unitPrice: b.unitPrice ?? b.UnitPrice ?? 0,
          });
        }
        setBom(rows);
      } catch (e) { setBomError(e.message); setBom([]); }
    })();
  }, [show, systemPart]);

  // /api/Part/{system} returns no mtlPartDescription for these components —
  // measured 35 of 35 blank on S9CEMENT — so the Description column was 35 rows
  // of nothing taking width from Part and Qty. Hide the column when the whole
  // list is blank, and show it the moment the backend starts sending them.
  const hasDesc = useMemo(() => (bom || []).some((b) => (b.desc || "").trim()), [bom]);

  const selected = useMemo(
    () => Object.keys(sel).map((p) => ({ ...(bom || []).find((b) => b.partNum === p), partNum: p, qty: sel[p] })),
    [sel, bom]
  );
  const visibleBom = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (bom || []).filter((b) => !f || b.partNum.toLowerCase().includes(f) || (b.desc || "").toLowerCase().includes(f));
  }, [bom, filter]);

  // A backorder is what the customer is still OWED, so it cannot exceed what
  // they bought: the component's per-system quantity times the number of systems
  // on this order line. `bump` had a floor of 1 and no ceiling at all, so + could
  // be clicked indefinitely — a rep could put 40 of a part the system contains
  // one of onto the order, at $0, and the modal would submit it.
  // Anything above the cap is a different transaction (a replacement or an RMA),
  // not a backorder.
  const maxFor = useCallback((p) => {
    const b = (bom || []).find((x) => x.partNum === p);
    return Math.max(1, (Number(b?.qtyPer) || 1) * (Number(systemQty) || 1));
  }, [bom, systemQty]);

  const toggle = (p) => setSel((s) => { const n = { ...s }; if (n[p] !== undefined) delete n[p]; else n[p] = 1; return n; });
  const bump = (p, d) => setSel((s) => (
    s[p] === undefined ? s : { ...s, [p]: Math.min(maxFor(p), Math.max(1, s[p] + d)) }
  ));

  const submit = useCallback(async () => {
    setSubmitting(true); setFailure(null);
    try {
      const r = await apiFetch(`${API_URL}/api/sales-orders/${encodeURIComponent(orderNum)}/backorder-lines`, {
        method: "POST",
        body: JSON.stringify({ lines: selected.map((x) => ({ partNum: x.partNum, qty: x.qty })), reason: note || "" }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok || d?.success === false) { setFailure(d || { error: `Request failed (HTTP ${r.status}).`, results: [] }); setStep("error"); return; }

      const added = (d.results || []).filter((x) => x.status === "Added").length;
      const present = (d.results || []).filter((x) => x.status === "AlreadyPresent").length;

      // Then, and only then: recalc tax (even at $0 — keeps the order's tax state consistent).
      if (added > 0) {
        await apiFetch(`${API_URL}/api/sales-orders/${encodeURIComponent(orderNum)}/calculate-tax`, { method: "POST" }).catch(() => {});
      }
      let msg = `${added} backorder line${added === 1 ? "" : "s"} added`;
      if (present > 0) msg += ` · ${present} already on the order`;
      onDone?.(`${msg} — no charge.`, added > 0);
      onHide();
    } catch (e) {
      setFailure({ error: e.message, results: [] }); setStep("error");
    } finally { setSubmitting(false); }
  }, [orderNum, selected, note, onDone, onHide]);

  const stepChip = (labels) => (
    <div style={{ display: "flex", gap: 8, marginBottom: 12, fontSize: 12, fontWeight: 700 }}>
      {["Select", "Review", "Confirm"].map((s, i) => {
        const cur = step === "error" ? 2 : ["select", "review", "confirm"].indexOf(step);
        return (
          <span key={s} style={{ color: i < cur ? "#16A34A" : i === cur ? "#4F46E5" : "#94A3B8" }}>
            {i + 1} · {s}{i < 2 ? "  ›" : ""}
          </span>
        );
      })}
    </div>
  );

  const statusBadge = (s) =>
    s === "Rejected" ? <Badge bg="danger">Rejected</Badge>
      : s === "RolledBack" ? <Badge bg="secondary">RolledBack</Badge>
      : s === "AlreadyPresent" ? <Badge bg="info">AlreadyPresent</Badge>
      : <Badge bg="success">{s}</Badge>;

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} size="lg" centered scrollable>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          Backorder parts · <span className="qw-mono">{systemPart}</span>
          <div style={{ fontSize: 12, fontWeight: 400, color: "#64748B" }}>
            Order #{orderNum} · tick the components on backorder. Each is added at $0 (100% discount).
          {Number(systemQty) > 1
            ? ` This line carries ${systemQty} systems, so a component caps at ${systemQty}\u00d7 its Qty/sys.`
            : " A component caps at its Qty/sys — that is all the order owes."}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {stepChip()}

        {step === "select" && (
          bom === null ? (
            <div style={{ textAlign: "center", padding: 30, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading components…</div>
          ) : bomError ? (
            <Alert variant="danger" style={{ fontSize: 13 }}>{bomError}</Alert>
          ) : (
            <>
              <Form.Control size="sm" placeholder="Filter components…" value={filter}
                onChange={(e) => setFilter(e.target.value)} style={{ marginBottom: 10 }} />
              <Table size="sm" hover style={{ fontSize: 12.5 }}>
                <thead><tr><th /><th>Part</th>{hasDesc && <th>Description</th>}<th className="text-end">Qty/sys</th><th>Qty to add</th></tr></thead>
                <tbody>
                  {visibleBom.map((b) => {
                    const on = sel[b.partNum] !== undefined;
                    return (
                      <tr key={b.partNum} style={{ cursor: "pointer", background: on ? "#EEF2FF" : undefined }} onClick={() => toggle(b.partNum)}>
                        <td style={{ width: 30 }}><Form.Check type="checkbox" checked={on} readOnly /></td>
                        <td style={{ fontWeight: 700 }}>{b.partNum}</td>
                        {hasDesc && <td style={{ color: "#64748B" }}>{b.desc}</td>}
                        <td className="text-end">{b.qtyPer}</td>
                        <td style={{ width: 120 }} onClick={(e) => e.stopPropagation()}>
                          {on ? (
                            <span style={{ display: "inline-flex", alignItems: "center", border: "1px solid #E2E8F0", borderRadius: 7 }}>
                              <Button variant="link" size="sm" style={{ padding: "0 8px", textDecoration: "none" }}
                                disabled={sel[b.partNum] <= 1} onClick={() => bump(b.partNum, -1)}>−</Button>
                              <span style={{ minWidth: 26, textAlign: "center" }}>{sel[b.partNum]}</span>
                              <Button variant="link" size="sm" style={{ padding: "0 8px", textDecoration: "none" }}
                                title={sel[b.partNum] >= maxFor(b.partNum) ? `The order carries ${maxFor(b.partNum)} of this component — a backorder cannot exceed it.` : undefined}
                                disabled={sel[b.partNum] >= maxFor(b.partNum)} onClick={() => bump(b.partNum, 1)}>+</Button>
                            </span>
                          ) : <span style={{ color: "#94A3B8" }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <Form.Group>
                <Form.Label style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase" }}>Reason / note (optional)</Form.Label>
                <Form.Control as="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Cup + calcar blades on backorder from vendor." style={{ fontSize: 12.5 }} />
              </Form.Group>
            </>
          )
        )}

        {step === "review" && (
          <>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 10 }}>
              {selected.length} line(s) to add · edit qty, remove a part, or change the note. Nothing saved yet.
            </div>
            {selected.map((x) => (
              <div key={x.partNum} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E2E8F0", borderRadius: 9, padding: "8px 12px", marginBottom: 8, background: "#F8FAFC" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{x.partNum} <Badge bg="info">$0.00</Badge></div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>{x.desc}</div>
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", border: "1px solid #E2E8F0", borderRadius: 7, background: "#fff" }}>
                  <Button variant="link" size="sm" style={{ padding: "0 8px", textDecoration: "none" }}
                    disabled={x.qty <= 1} onClick={() => bump(x.partNum, -1)}>−</Button>
                  <span style={{ minWidth: 26, textAlign: "center" }}>{x.qty}</span>
                  <Button variant="link" size="sm" style={{ padding: "0 8px", textDecoration: "none" }}
                    disabled={x.qty >= maxFor(x.partNum)} onClick={() => bump(x.partNum, 1)}>+</Button>
                </span>
                <Button variant="outline-danger" size="sm" onClick={() => toggle(x.partNum)}>×</Button>
              </div>
            ))}
            <Form.Group>
              <Form.Label style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase" }}>Note</Form.Label>
              <Form.Control as="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={{ fontSize: 12.5 }} />
            </Form.Group>
          </>
        )}

        {step === "confirm" && (
          <div style={{ border: "1px solid #ECD6A8", background: "#FBF2E0", borderRadius: 11, padding: "13px 15px", fontSize: 13 }}>
            <div style={{ fontWeight: 700, color: "#A9701A", marginBottom: 8 }}>Here's what this does</div>
            <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
              <li><b>{selected.length} new line(s)</b> added at <b>$0.00</b> each (100% backorder discount), comment "Back order discount given."</li>
              <li><b>No-charge</b> — the customer is <b>not billed</b>.</li>
              <li>Ships as a <b>separate shipment</b> and produces a <b>$0 invoice</b> that auto-suppresses: no email, no reminder, no amount due.</li>
              <li>Order total is <b>unchanged</b>.</li>
            </ol>
          </div>
        )}

        {step === "error" && failure && (
          <>
            <Alert variant="danger" style={{ fontSize: 13 }}>
              <b>Nothing was added.</b> {failure.error} All-or-nothing: fix or remove the flagged part and submit again.
            </Alert>
            {(failure.results || []).map((r) => (
              <div key={r.partNum} style={{ display: "flex", alignItems: "center", gap: 9, border: "1px solid #E2E8F0", borderRadius: 8, padding: "7px 10px", marginBottom: 6, fontSize: 12.5 }}>
                <span className="qw-mono" style={{ fontWeight: 700 }}>{r.partNum}</span>
                <span style={{ color: "#64748B", flex: 1 }}>{r.reason ? `— ${r.reason}` : ""}</span>
                {statusBadge(r.status)}
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === "select" && (
          <>
            <span style={{ marginRight: "auto", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{selected.length} selected</span>
            <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
            <Button variant="primary" disabled={selected.length === 0} onClick={() => setStep("review")}>Review →</Button>
          </>
        )}
        {step === "review" && (
          <>
            <Button variant="outline-secondary" onClick={() => setStep("select")}>← Back</Button>
            <Button variant="primary" disabled={selected.length === 0} onClick={() => setStep("confirm")}>Continue →</Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button variant="outline-secondary" disabled={submitting} onClick={() => setStep("review")}>← Back</Button>
            <Button variant="warning" disabled={submitting} onClick={submit} style={{ fontWeight: 700 }}>
              {submitting ? <><Spinner animation="border" size="sm" /> Adding…</> : "Add these lines"}
            </Button>
          </>
        )}
        {step === "error" && (
          <>
            <Button variant="outline-secondary" disabled={submitting} onClick={() => setStep("review")}>← Back to review</Button>
            <Button variant="primary" disabled={submitting} onClick={submit}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Try again"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
